import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { Icon } from "../components/Icon.js";

type Phase = "prompt" | "loading" | "result" | "error";

const PRESETS: { label: string; instruction: string }[] = [
  { label: "Improve clarity", instruction: "Improve the clarity of this passage without changing its meaning." },
  { label: "Reduce repetition", instruction: "Reduce repetition and tighten the prose." },
  { label: "Less sentimental", instruction: "Make this less sentimental and more restrained." },
  { label: "Tighten", instruction: "Tighten this paragraph; cut anything that isn't earning its place." },
  { label: "Improve dialogue", instruction: "Improve the dialogue so it feels more natural." },
  { label: "Expand this beat", instruction: "Expand this beat with a little more interiority and sensory detail." }
];

/**
 * Ask Scribe: generate a proposed revision of the selected text from a reviewer
 * instruction (reuses the existing "rewrite" AI mode), shown as BEFORE/AFTER.
 * Nothing is applied here — the parent applies the accepted text through the safe
 * save path. Reject leaves the manuscript untouched.
 */
export function AskScribe({
  projectId,
  sceneId,
  selectedText,
  onCancel,
  onAccept,
  applying
}: {
  projectId: string;
  sceneId: string;
  selectedText: string;
  onCancel: () => void;
  onAccept: (after: string) => void;
  applying: boolean;
}) {
  const [phase, setPhase] = useState<Phase>("prompt");
  const [instruction, setInstruction] = useState("");
  const [after, setAfter] = useState("");
  const [error, setError] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { ref.current?.focus(); }, []);

  const generate = async (text: string) => {
    const instr = text.trim();
    setPhase("loading");
    try {
      const res = await api.aiMessage(projectId, { mode: "rewrite", sceneId, selectedText, message: instr || null });
      const reply = (res.reply ?? "").trim();
      if (!reply) throw new Error("Scribe returned nothing to apply.");
      setAfter(reply);
      setPhase("result");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Scribe couldn't generate a revision.");
      setPhase("error");
    }
  };

  return (
    <div className="review-composer-backdrop" onMouseDown={applying ? undefined : onCancel}>
      <div className="review-ask card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="review-composer-head"><Icon name="sparkles" size={16} /> Ask Scribe</div>

        <div className="review-ask-before">
          <div className="review-ask-label">Selected</div>
          <blockquote className="review-composer-quote">
            “{selectedText.length > 400 ? selectedText.slice(0, 400) + "…" : selectedText}”
          </blockquote>
        </div>

        {phase === "prompt" && (
          <>
            <div className="review-ask-presets">
              {PRESETS.map((p) => (
                <button key={p.label} className="review-cat-chip" onClick={() => void generate(p.instruction)}>{p.label}</button>
              ))}
            </div>
            <textarea ref={ref} className="input review-composer-text" rows={3}
              placeholder="Or type an instruction — e.g. “rewrite this with less obvious emotion”…"
              value={instruction} onChange={(e) => setInstruction(e.target.value)}
              onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === "Enter") void generate(instruction); }} />
            <div className="row gap-2" style={{ justifyContent: "flex-end" }}>
              <button className="btn btn-sm btn-ghost" onClick={onCancel}>Cancel</button>
              <button className="btn btn-sm btn-primary" onClick={() => void generate(instruction)}>Generate</button>
            </div>
          </>
        )}

        {phase === "loading" && (
          <div className="review-ask-loading"><Icon name="sparkles" size={18} className="spin" /> Scribe is drafting a revision…</div>
        )}

        {phase === "error" && (
          <>
            <div className="ab-error" style={{ marginTop: 4 }}><Icon name="alert" size={15} /> {error}</div>
            <div className="row gap-2" style={{ justifyContent: "flex-end" }}>
              <button className="btn btn-sm btn-ghost" onClick={onCancel}>Close</button>
              <button className="btn btn-sm btn-primary" onClick={() => setPhase("prompt")}>Try again</button>
            </div>
          </>
        )}

        {phase === "result" && (
          <>
            <div className="review-ask-after">
              <div className="review-ask-label">Suggested</div>
              <div className="review-ask-suggestion">{after}</div>
            </div>
            <p className="faint" style={{ fontSize: 11.5, margin: 0 }}>
              Accepting creates a version snapshot, saves, and reindexes — fully reversible from history. Rejecting changes nothing.
            </p>
            <div className="row gap-2" style={{ justifyContent: "flex-end" }}>
              <button className="btn btn-sm btn-ghost" onClick={onCancel} disabled={applying}>Reject</button>
              <button className="btn btn-sm btn-ghost" onClick={() => setPhase("prompt")} disabled={applying}>
                <Icon name="undo" size={13} /> Regenerate
              </button>
              <button className="btn btn-sm btn-primary" onClick={() => onAccept(after)} disabled={applying}>
                {applying ? <><Icon name="clock" size={13} className="spin" /> Applying…</> : <><Icon name="check" size={13} /> Accept & save</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
